import { getToken } from '@/utils/auth' // get token from cookie
import getPageTitle from '@/utils/get-page-title'
import { Message } from 'element-ui'
import NProgress from 'nprogress' // progress bar
import 'nprogress/nprogress.css' // progress bar style
import router from './router'
import store from './store'

NProgress.configure({ showSpinner: false }) // NProgress Configuration
const whiteList = ['/login', '/auth-redirect'] // no redirect whitelist

function normalizePath(path) {
  return path.replace(/\/+/g, '/')
}

function joinRoutePath(parentPath, routePath) {
  if (!routePath) {
    return parentPath || '/'
  }
  if (routePath.startsWith('/')) {
    return routePath
  }
  if (!parentPath || parentPath === '/') {
    return normalizePath(`/${routePath}`)
  }
  return normalizePath(`${parentPath}/${routePath}`)
}

function hasAccessiblePath(routes, targetPath, parentPath = '') {
  return routes.some(route => {
    const currentPath = joinRoutePath(parentPath, route.path)
    if (currentPath === targetPath) {
      return true
    }
    if (route.children && route.children.length) {
      return hasAccessiblePath(route.children, targetPath, currentPath)
    }
    return false
  })
}

function getFirstAccessiblePath(routes, parentPath = '') {
  for (const route of routes) {
    const currentPath = joinRoutePath(parentPath, route.path)
    if (route.redirect && route.redirect !== 'noRedirect' && !route.children) {
      return route.redirect
    }
    if (route.children && route.children.length) {
      const childPath = getFirstAccessiblePath(route.children, currentPath)
      if (childPath) {
        return childPath
      }
    }
    if (!route.hidden && route.path !== '*' && currentPath !== '/404') {
      return currentPath
    }
  }
  return '/'
}

router.beforeEach(async(to, from, next) => {
  // start progress bar
  NProgress.start()

  // set page title
  document.title = getPageTitle(to.meta.title)

  // determine whether the user has logged in
  const hasToken = getToken()

  if (hasToken) {
    if (to.path === '/login') {
      // if is logged in, redirect to the home page
      next({ path: '/' })
      NProgress.done()
    } else {
      // determine whether the user has obtained his permission roles through getInfo
      const hasRoles = store.getters.roles && store.getters.roles.length > 0
      if (hasRoles) {
        next()
      } else {
        try {
          // get user info
          // note: roles must be a object array! such as: ['admin'] or ,['developer','editor']
          const userInfo = await store.dispatch('user/getInfo')

          // generate accessible routes map based on roles
          const accessRoutes = await store.dispatch('permission/generateRoutes', {
            roles: userInfo.roles || [],
            routePermissions: userInfo.routePermissions || []
          })

          // dynamically add accessible routes
          router.addRoutes(accessRoutes)

          const canAccessRedirect = hasAccessiblePath(accessRoutes, to.path)
          const nextPath = canAccessRedirect
            ? to.path
            : getFirstAccessiblePath(accessRoutes)

          // hack method to ensure that addRoutes is complete
          // set the replace: true, so the navigation will not leave a history record
          next({
            path: nextPath,
            query: canAccessRedirect ? to.query : {},
            replace: true
          })
        } catch (error) {
          // remove token and go to login page to re-login
          await store.dispatch('user/resetToken')
          Message.error(error || 'Has Error')
          next(`/login?redirect=${to.path}`)
          NProgress.done()
        }
      }
    }
  } else {
    /* has no token*/
    if (whiteList.indexOf(to.path) !== -1) {
      // in the free login whitelist, go directly
      next()
    } else {
      // other pages that do not have permission to access are redirected to the login page.
      next(`/login?redirect=${to.path}`)
      NProgress.done()
    }
  }
})

router.afterEach(() => {
  // finish progress bar
  NProgress.done()
})
